import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Search, Plus, MapPin, ChevronRight, MoreVertical, Edit, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { AddPersonDialog, EditPersonDialog } from "@/components/EntityDialogs";
import { toast } from "sonner";

const roleFilters = ["All", "Principal", "Spouse", "Ex-spouse", "Child", "Staff", "Family", "Archived"];

export default function People() {
  const navigate = useNavigate();
  const { people, updatePerson, deletePerson } = useFgp();
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editPerson, setEditPerson] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const filtered = people.filter((p) => {
    if (!p.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "Archived") return p.archived;
    if (p.archived) return false;
    return filter === "All" || p.role === filter;
  });

  const archiveCount = people.filter((p) => p.archived).length;

  return (
    <div>
      <PageHeader
        title="People Directory"
        subtitle="Everyone in the network — principal, family and staff. Add, edit, archive or remove."
        actions={<Button onClick={() => setAddOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-person"><Plus className="h-4 w-4" /> Add person</Button>}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="pl-9 bg-white" data-testid="people-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {roleFilters.map((r) => (
            <button key={r} onClick={() => setFilter(r)} data-testid={`filter-${r}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === r ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {r}{r === "Archived" && archiveCount > 0 ? ` (${archiveCount})` : ""}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <p className="text-sm font-medium text-slate-700">{filter === "Archived" ? "No archived people" : "No people found"}</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">{filter === "Archived" ? "Archived people will appear here." : "Add someone to your principal's network."}</p>
          {filter !== "Archived" && <Button onClick={() => setAddOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="empty-add-person"><Plus className="h-4 w-4" /> Add person</Button>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              data-testid={`person-card-${p.id}`}
              className={`group relative bg-white rounded-xl border shadow-sm p-5 hover:shadow-md transition-all ${p.archived ? "border-slate-200 opacity-70" : "border-slate-200 hover:border-neutral-200"}`}
            >
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/app/people/${p.id}`)}>
                <Avatar className="h-12 w-12 ring-2 ring-white shadow">
                  <AvatarImage src={p.avatar} alt={p.name} />
                  <AvatarFallback>{p.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                  <p className="text-xs text-slate-400 truncate">{p.title}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-neutral-500 transition-colors" />
              </div>

              <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-500">
                <span className={`px-2 py-0.5 rounded-full font-medium ${p.role === "Staff" ? "bg-slate-100 text-slate-600" : "bg-neutral-50 text-neutral-700"}`}>{p.role}</span>
                {p.relation && <span className="text-slate-400">· {p.relation}</span>}
                {p.archived && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium" data-testid={`archived-badge-${p.id}`}>Archived</span>}
              </div>
              {p.location && p.location !== "—" && (
                <p className="mt-2 text-xs text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</p>
              )}

              <div className="absolute top-3 right-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`person-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditPerson(p)} data-testid={`person-edit-${p.id}`}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                    {p.archived ? (
                      <DropdownMenuItem onClick={() => { updatePerson(p.id, { archived: false }); toast.success(`${p.name} restored`); }} data-testid={`person-unarchive-${p.id}`}><ArchiveRestore className="h-4 w-4 mr-2" /> Unarchive</DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => { updatePerson(p.id, { archived: true }); toast.success(`${p.name} archived`); }} data-testid={`person-archive-${p.id}`}><Archive className="h-4 w-4 mr-2" /> Archive</DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setToDelete(p)} className="text-rose-600 focus:text-rose-600" data-testid={`person-delete-${p.id}`}><Trash2 className="h-4 w-4 mr-2" /> Remove</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AddPersonDialog open={addOpen} onOpenChange={setAddOpen} />
      <EditPersonDialog open={!!editPerson} onOpenChange={(o) => { if (!o) setEditPerson(null); }} person={editPerson} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-person-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes them from the directory and relationship view. Consider archiving instead if you may need them later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-person"
              onClick={() => { deletePerson(toDelete.id); toast.success(`${toDelete.name} removed`); setToDelete(null); }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
